import {createSlice, type PayloadAction} from "@reduxjs/toolkit";

import {isLifeCycleAtLeast, RevaLifeCycleState} from "@reva/shared/lifecycle-state";
import type {SnapshotMetadata, ValuationData, ValuationMetadata} from "@reva/shared/valuation-data";
import {effectivePurpose, purposePolicy} from "@reva/shared/valuation-purpose";

import type {QueryError} from "../util/errorUtil";
import {buildApiQueryError} from "../util/errorUtil";
import type {ApiQueryError} from "./types";

/** A user a valuation can be assigned to (owner). */
export interface AssignableUser {
    username: string;
    fullName: string | null;
}

interface PersistenceState {
    initialized: boolean;
    loading: boolean;
    immobilienId: string | null;
    revaId: string | null;
    valuationMetadata: ValuationMetadata | null;
    snapshots: SnapshotMetadata[] | null;
    /** True if the loaded valuation is a frozen snapshot. */
    isSnapshot: boolean;
    /** Metadata of the loaded snapshot; only set while {@link isSnapshot} is true. */
    snapshotMetadata: SnapshotMetadata | null;
    initialValuationExecuted: string | null;
    queryError: ApiQueryError | null;
    finalizationError: ApiQueryError | null;
    finalizationLoading: boolean;
    preFinalizeError: ApiQueryError | null;
    preFinalizeLoading: boolean;
    /** Loading/error of a reload of the CURRENTLY loaded valuation (see reloadPersistence thunk). */
    reloadLoading: boolean;
    reloadError: ApiQueryError | null;
    persistenceSingleParamLoading: boolean;
    persistenceSingleParamError: ApiQueryError | null;
    lastValidPersistenceResponse: ValuationData | null;
    /**
     * Anzahl der gerade laufenden Schreibvorgänge auf der Bewertung. Bewusst ein Zähler und kein Flag:
     * ein einziger Klick kann mehrere Parameter parallel speichern (z. B. „Bodenrichtwert übernehmen"),
     * und ein Boolean würde beim ersten Rückläufer schon „fertig" melden, während die übrigen noch
     * unterwegs sind. Die Finalisierungs-Dialoge warten darauf, dass der Zähler auf 0 steht, bevor sie
     * ihren Vergleichsstand nehmen.
     */
    pendingWrites: number;
    /**
     * Ob gerade ein Segment-Override geschrieben wird (#19444). Eigenes Flag statt
     * {@link PersistenceState.persistenceSingleParamLoading}, weil das bei jedem Einzelparameter
     * anspringt — das Segment-Control würde dann auch beim Tippen in einem anderen Feld beschäftigt
     * aussehen. Der Wechsel hängt am Segment-Control und wirkt auf die ganze Maske, deshalb braucht
     * genau dieses Control eine eigene Rückmeldung.
     */
    segmentWritePending: boolean;
    /** Sequenznummer der zuletzt übernommenen Antwort, siehe {@link nextPersistenceRequestSeq}. */
    lastAppliedResponseSeq: number;
    /**
     * Gesetzt, wenn {@link resetPersistenceToLastValidResponse} zurückgesetzt hat, während noch Schreibvorgänge
     * liefen (#19774): der Wert ist {@link lastAppliedResponseSeq} zum Zeitpunkt des Zurücksetzens. Die
     * Antworten dieser Schreibvorgänge kennt der zurückgesetzte Stand nicht, und Einzel-PATCH-Antworten werden
     * nicht auf die Maske angewendet. Sobald keine Schreibvorgänge mehr laufen und kein Fehler offen ist,
     * wird deshalb erneut auf die dann neueste Antwort zurückgesetzt. Sonst null.
     */
    resyncAfterWritesFromSeq: number | null;
    assignableUsersLoading: boolean;
    assignableUsers: AssignableUser[] | null;
}

const initialState: PersistenceState = {
    initialized: false,
    loading: false,
    immobilienId: null,
    revaId: null,
    valuationMetadata: null,
    snapshots: null,
    isSnapshot: false,
    snapshotMetadata: null,
    initialValuationExecuted: null,
    queryError: null,
    finalizationError: null,
    finalizationLoading: false,
    preFinalizeError: null,
    preFinalizeLoading: false,
    reloadLoading: false,
    reloadError: null,
    persistenceSingleParamLoading: false,
    persistenceSingleParamError: null,
    lastValidPersistenceResponse: null,
    pendingWrites: 0,
    segmentWritePending: false,
    lastAppliedResponseSeq: 0,
    resyncAfterWritesFromSeq: null,
    assignableUsersLoading: false,
    assignableUsers: null,
};

/**
 * Laufende Nummer für Persistenz-Anfragen. Sie wird beim ABSENDEN gezogen und mit der Antwort wieder an
 * {@link setUpdatedValuationResponse} übergeben, damit eine überholte, ältere Antwort den Store nicht
 * zurückdrehen kann: die Antworten kommen nicht zwingend in der Reihenfolge zurück, in der sie
 * abgeschickt wurden (die Requests eines Browsers verteilen sich per Round-Robin über mehrere
 * reva-rest-Knoten), und jede Antwort enthält den KOMPLETTEN Stand der Bewertung.
 *
 * Bewusst modulglobal und ohne Reset: die Nummer muss über Bewertungs- und Snapshot-Wechsel hinweg
 * monoton wachsen, sonst könnte eine alte Antwort nach einem Wechsel wieder gewinnen.
 *
 * Nur im Dev-Server relevant: bei einem Hot-Module-Replacement dieses Moduls startet der Zähler wieder
 * bei 0, während lastAppliedResponseSeq im Store stehen bleibt — dann werden Antworten so lange
 * verworfen, bis der Zähler wieder aufgeholt hat. Ein Reload der Seite behebt das; im Build kann es
 * nicht auftreten.
 */
let persistenceRequestSeq = 0;

/**
 * @return die nächste Sequenznummer für eine Persistenz-Anfrage — direkt vor dem Absenden ziehen,
 *         nicht erst beim Eintreffen der Antwort.
 */
export function nextPersistenceRequestSeq(): number {
    return ++persistenceRequestSeq;
}

export const persistenceSlice = createSlice({
    name: "persistence",
    initialState,
    reducers: {
        resetPersistence: (state) => {
            state.loading = false;
            state.revaId = null;
            state.valuationMetadata = null;
            state.snapshots = null;
            state.isSnapshot = false;
            state.snapshotMetadata = null;
            state.initialValuationExecuted = null;
            state.initialized = true;
            state.queryError = null;

            state.finalizationLoading = false;
            state.finalizationError = null;

            state.preFinalizeLoading = false;
            state.preFinalizeError = null;

            state.reloadLoading = false;
            state.reloadError = null;

            state.persistenceSingleParamLoading = false;
            state.persistenceSingleParamError = null;
            state.lastValidPersistenceResponse = null;
            // Nur den Zähler der laufenden Schreibvorgänge zurücksetzen; lastAppliedResponseSeq bleibt
            // absichtlich stehen, weil die Sequenz global monoton ist — sonst könnte eine noch fliegende
            // Antwort der alten Bewertung nach dem Wechsel wieder gewinnen.
            state.pendingWrites = 0;
            state.segmentWritePending = false;
            state.resyncAfterWritesFromSeq = null;
            state.assignableUsers = null;
            state.assignableUsersLoading = false;
        },
        setRevaId: (state, action: PayloadAction<string>) => {
            state.initialized = true;
            state.revaId = action.payload;
            state.queryError = null;

            state.finalizationError = null;
            state.finalizationLoading = false;

            state.persistenceSingleParamError = null;
            state.persistenceSingleParamLoading = false;
        },
        setPersistenceLoading: (state) => {
            state.loading = true;
            state.revaId = null;
            state.queryError = null;
        },
        setPersistenceLoadingFinished: (state) => {
            state.loading = false;
        },
        setPersistenceSingleParamLoading: (state, action: PayloadAction<boolean>) => {
            const persistenceSingleParamLoading = action.payload;

            state.persistenceSingleParamLoading = persistenceSingleParamLoading;

            if (persistenceSingleParamLoading) {
                state.persistenceSingleParamError = null;
                state.finalizationError = null;
            }
        },
        /** Ein Schreibvorgang auf der Bewertung startet — siehe {@link PersistenceState.pendingWrites}. */
        beginPersistenceWrite: (state) => {
            state.pendingWrites += 1;
        },
        /** Ein Schreibvorgang ist beendet, egal ob erfolgreich oder mit Fehler. */
        endPersistenceWrite: (state) => {
            state.pendingWrites = Math.max(0, state.pendingWrites - 1);
        },
        /** Setzt oder löscht {@link PersistenceState.resyncAfterWritesFromSeq}. */
        setResyncAfterWritesFromSeq: (state, action: PayloadAction<number | null>) => {
            state.resyncAfterWritesFromSeq = action.payload;
        },
        /** Das Schreiben des Segment-Overrides startet — siehe {@link PersistenceState.segmentWritePending}. */
        beginSegmentWrite: (state) => {
            state.segmentWritePending = true;
        },
        /** Das Schreiben des Segment-Overrides ist beendet, egal ob erfolgreich oder mit Fehler. */
        endSegmentWrite: (state) => {
            state.segmentWritePending = false;
        },
        setFinalizationLoading: (state, action: PayloadAction<boolean>) => {
            const finalizationLoading = action.payload;

            state.finalizationLoading = finalizationLoading;

            if (finalizationLoading) {
                state.finalizationError = null;
            }
        },
        setInitialized: (state) => {
            state.initialized = true;
        },
        setQueryError: (state, action: PayloadAction<QueryError>) => {
            state.initialized = true;
            state.loading = false;
            state.revaId = null;
            state.immobilienId = null;
            state.valuationMetadata = null;
            state.snapshots = null;
            state.isSnapshot = false;
            state.snapshotMetadata = null;
            state.initialValuationExecuted = null;
            state.queryError = buildApiQueryError(action.payload);
            state.lastValidPersistenceResponse = null;
        },
        setFinalizationError: (state, action: PayloadAction<QueryError>) => {
            state.finalizationLoading = false;
            state.finalizationError = buildApiQueryError(action.payload);
        },
        setPersistenceSingleParamError: (state, action: PayloadAction<QueryError>) => {
            state.initialized = true;
            state.persistenceSingleParamLoading = false;

            state.persistenceSingleParamError = buildApiQueryError(action.payload);
        },
        setUpdatedValuationResponse: (state, action: PayloadAction<{response: ValuationData; seq: number}>) => {
            const {response, seq} = action.payload;

            // Überholte Antwort verwerfen: zu einer später abgeschickten Anfrage ist die Antwort bereits
            // übernommen. Ohne diese Prüfung gewinnt die zuletzt EINTREFFENDE statt der zuletzt
            // ABGESCHICKTEN Antwort, und der Store fällt auf einen älteren Vollstand zurück — inklusive
            // der Änderung, die der Benutzer gerade selbst gespeichert hat.
            if (seq < state.lastAppliedResponseSeq) {
                return;
            }
            state.lastAppliedResponseSeq = seq;

            const result: ValuationData = {...response};

            // Snapshots are included on load/create and by the operations that change them
            // ((pre-)finalize) — keep the previously loaded list when a response does not carry them.
            state.snapshots = result.snapshots ?? state.snapshots;

            state.lastValidPersistenceResponse = result;

            state.immobilienId = result.immobilienId || null;
            state.initialValuationExecuted = result.initialValuationExecuted || null;
            // Die zuweisbaren Owner hängen vom Zustand ab (PreFinal/Correction nur Abschlussberechtigte, #19302 Z-B2):
            // bei einem Zustandswechsel verwerfen, AssignUserDropdown lädt sie dann neu.
            if (state.valuationMetadata && state.valuationMetadata.lifeCycleState !== result.metadata?.lifeCycleState) {
                state.assignableUsers = null;
            }
            state.valuationMetadata = result.metadata;

            // Snapshot context: live/update responses carry isSnapshot=false and no snapshotMetadata at all,
            // so this self-corrects back to "live" as soon as a non-snapshot response arrives.
            state.isSnapshot = result.isSnapshot;
            state.snapshotMetadata = result.snapshotMetadata ?? null;
        },
        removePersistenceQueryError: (state) => {
            state.queryError = null;
        },
        removeFinalizationError: (state) => {
            state.finalizationError = null;
        },
        setPreFinalizeLoading: (state, action: PayloadAction<boolean>) => {
            const preFinalizeLoading = action.payload;
            state.preFinalizeLoading = preFinalizeLoading;
            if (preFinalizeLoading) {
                state.preFinalizeError = null;
            }
        },
        setPreFinalizeError: (state, action: PayloadAction<QueryError>) => {
            state.preFinalizeLoading = false;
            state.preFinalizeError = buildApiQueryError(action.payload);
        },
        removePreFinalizeError: (state) => {
            state.preFinalizeError = null;
        },
        setReloadLoading: (state, action: PayloadAction<boolean>) => {
            state.reloadLoading = action.payload;
            if (action.payload) {
                state.reloadError = null;
            }
        },
        setReloadError: (state, action: PayloadAction<QueryError>) => {
            state.reloadLoading = false;
            state.reloadError = buildApiQueryError(action.payload);
        },
        clearReloadError: (state) => {
            state.reloadError = null;
        },
        removePersistenceSingleParamError: (state) => {
            state.persistenceSingleParamError = null;
        },
        setAssignableUsersLoading: (state, action: PayloadAction<boolean>) => {
            state.assignableUsersLoading = action.payload;
        },
        setAssignableUsers: (state, action: PayloadAction<AssignableUser[]>) => {
            state.assignableUsers = action.payload;
        },
    },
    selectors: {
        selectPersistenceLoading: (state) => state.loading,
        selectRevaId: (state) => state.revaId,
        selectImmobilienId: (state) => state.immobilienId,
        /**
         * True if the loaded valuation is currently in the "Final" lifecycle state.
         * Use only for Final-specific display/controls (e.g. the correction entry point);
         * for edit-gating use {@link selectReadOnly} instead.
         */
        selectLifeCycleStateFinal: (state) => state.valuationMetadata?.lifeCycleState === RevaLifeCycleState.Final,
        /** Current lifecycle state of the loaded valuation, or null if none is loaded. */
        selectLifeCycleState: (state) => state.valuationMetadata?.lifeCycleState ?? null,
        /**
         * True when editing the valuation is currently forbidden: no persistence updates and no
         * Finalize/PreFinalize workflow operations. True exactly when a snapshot is loaded
         * OR the lifeCycleState === "Final".
         */
        selectReadOnly: (state) =>
            state.isSnapshot || state.valuationMetadata?.lifeCycleState === RevaLifeCycleState.Final,
        /**
         * True if the georef of the loaded valuation must no longer be refreshed ("Neue Georeferenzierung erzwingen"):
         * a snapshot is loaded or the valuation has at least been pre-finalized (PreFinal, Final, Correction). The
         * coordinates of a (pre-)finalized valuation stay fixed, and the external georef call with cache=false runs
         * before the REFRESH_GEOREF guard in reva-rest.
         */
        selectGeorefRefreshLocked: (state) =>
            state.isSnapshot ||
            isLifeCycleAtLeast(state.valuationMetadata?.lifeCycleState ?? null, RevaLifeCycleState.PreFinal),
        selectFinalizingUser: (state) => state.valuationMetadata?.finalizeUser,
        /** Falls back to finalizeUser if finalizeUserFullName is not set. */
        selectFinalizingUserFullName: (state) =>
            state.valuationMetadata?.finalizeUserFullName ?? state.valuationMetadata?.finalizeUser,
        selectCorrectionCounter: (state) => state.valuationMetadata?.correctionCounter,
        selectNeverBeenFinalized: (state) =>
            state.valuationMetadata?.lifeCycleState !== RevaLifeCycleState.Final &&
            !state.valuationMetadata?.correctionCounter,
        selectFirstFinalizeDate: (state) => state.valuationMetadata?.firstFinalizeDate,
        selectValuationMetadata: (state) => state.valuationMetadata,
        /** #19481: Zweck der geladenen REVA-ID (fehlendes Feld = Bewertung). */
        selectPurpose: (state) => effectivePurpose(state.valuationMetadata?.purpose),
        /**
         * #19481: Fähigkeiten des Zwecks der geladenen REVA-ID (stabile Referenz je Zweck). Die GUI fragt nur diese
         * ab, nie einen konkreten Zweck.
         */
        selectPurposePolicy: (state) => purposePolicy(state.valuationMetadata?.purpose),
        /** #19481: Zeitpunkt der automatischen Löschung ohne weitere Nutzung, sonst null. */
        selectRetentionExpiresAt: (state) => state.valuationMetadata?.retentionExpiresAt ?? null,
        /**
         * Snapshot metadata (number, type, date, person, comment) of the loaded valuation,
         * sorted ascending by number. Null if no valuation is loaded.
         */
        selectSnapshots: (state) => state.snapshots,
        /** True if the currently loaded valuation is a frozen snapshot. */
        selectIsSnapshot: (state) => state.isSnapshot,
        /** Metadata of the loaded snapshot, or null if the loaded valuation is live. */
        selectSnapshotMetadata: (state) => state.snapshotMetadata,
        /** Number of the loaded snapshot, or null if the loaded valuation is live. */
        selectLoadedSnapshotNumber: (state) => (state.isSnapshot ? (state.snapshotMetadata?.number ?? null) : null),
        /** True if the loaded snapshot is the newest (highest-numbered) snapshot of the valuation. */
        selectIsNewestSnapshotLoaded: (state) => {
            const loaded = state.isSnapshot ? state.snapshotMetadata?.number : null;
            if (loaded == null) return false;
            const numbers = (state.snapshots ?? []).map((s) => s.number);
            return numbers.length > 0 && loaded === Math.max(...numbers);
        },
        selectInitialValuationExecuted: (state) => state.initialValuationExecuted,
        selectPersistenceInitialized: (state) => state.initialized,
        selectFinalizationLoading: (state) => state.finalizationLoading,
        selectFinalizationError: (state) => state.finalizationError,
        selectPreFinalizeLoading: (state) => state.preFinalizeLoading,
        selectPreFinalizeError: (state) => state.preFinalizeError,
        selectReloadLoading: (state) => state.reloadLoading,
        selectReloadError: (state) => state.reloadError,
        selectPersistenceQueryError: (state) => state.queryError,
        selectPersistenceSingleParamError: (state) => state.persistenceSingleParamError,
        selectLastValidPersistenceResponse: (state) => state.lastValidPersistenceResponse,
        selectPendingWrites: (state) => state.pendingWrites,
        selectSegmentWritePending: (state) => state.segmentWritePending,
        selectLastAppliedResponseSeq: (state) => state.lastAppliedResponseSeq,
        selectResyncAfterWritesFromSeq: (state) => state.resyncAfterWritesFromSeq,
        selectAssignableUsers: (state) => state.assignableUsers,
        selectAssignableUsersLoading: (state) => state.assignableUsersLoading,
    },
});

export const {
    resetPersistence,
    setRevaId,
    setPersistenceLoading,
    setPersistenceLoadingFinished,
    setInitialized,
    setFinalizationLoading,
    setQueryError,
    setFinalizationError,
    removePersistenceQueryError,
    removeFinalizationError,
    setPreFinalizeLoading,
    setPreFinalizeError,
    removePreFinalizeError,
    setReloadLoading,
    setReloadError,
    clearReloadError,
    setPersistenceSingleParamLoading,
    beginPersistenceWrite,
    endPersistenceWrite,
    beginSegmentWrite,
    endSegmentWrite,
    setResyncAfterWritesFromSeq,
    setPersistenceSingleParamError,
    removePersistenceSingleParamError,
    setUpdatedValuationResponse,
    setAssignableUsersLoading,
    setAssignableUsers,
} = persistenceSlice.actions;

export const {
    selectPersistenceLoading,
    selectRevaId,
    selectImmobilienId,
    selectLifeCycleStateFinal,
    selectLifeCycleState,
    selectReadOnly,
    selectGeorefRefreshLocked,
    selectFinalizingUser,
    selectFinalizingUserFullName,
    selectCorrectionCounter,
    selectNeverBeenFinalized,
    selectFirstFinalizeDate,
    selectValuationMetadata,
    selectPurpose,
    selectPurposePolicy,
    selectRetentionExpiresAt,
    selectSnapshots,
    selectIsSnapshot,
    selectSnapshotMetadata,
    selectLoadedSnapshotNumber,
    selectIsNewestSnapshotLoaded,
    selectInitialValuationExecuted,
    selectPersistenceInitialized,
    selectFinalizationLoading,
    selectFinalizationError,
    selectPreFinalizeLoading,
    selectPreFinalizeError,
    selectReloadLoading,
    selectReloadError,
    selectPersistenceQueryError,
    selectPersistenceSingleParamError,
    selectLastValidPersistenceResponse,
    selectPendingWrites,
    selectSegmentWritePending,
    selectLastAppliedResponseSeq,
    selectResyncAfterWritesFromSeq,
    selectAssignableUsers,
    selectAssignableUsersLoading,
} = persistenceSlice.selectors;

export default persistenceSlice.reducer;
